import type { ApiSuccess, Paginated } from './api';
import type { BookingPaymentMethod } from './booking';
import type { RateCardUnit } from './rate-card';
import type { ResourceOperatingHour } from './resource';

export const FACILITY_PROPOSAL_STATUSES = [
  'submitted',
  'under_review',
  'awaiting_payment',
  'converted_to_booking',
  'rejected',
] as const;
export type FacilityProposalStatus = (typeof FACILITY_PROPOSAL_STATUSES)[number];

export interface ProposalResourceSummary {
  id: string;
  /** Stable resource slug. Proposal eligibility is controlled by the resource's booking mode. */
  slug: string;
  name: string;
  capacity: number;
  minimumBookingMinutes: number;
}

export interface GuestProposalResource extends ProposalResourceSummary {
  /** The space card copy, the same `shortDescription` the spaces API publishes. */
  shortDescription: string;
  operatingHours: ResourceOperatingHour[];
  rate: {
    itemId: string;
    name: string;
    amountMinor: number;
    currency: 'USD';
    unit: RateCardUnit;
  };
}

export interface CreateGuestProposalRequest {
  /** Client-generated UUID used to make retries idempotent. */
  requestId: string;
  resourceId: string;
  contact: {
    name: string;
    email: string;
    phone: string;
  };
  organizationName?: string;
  jobTitle?: string;
  purpose: string;
  bookingDate: string;
  startMinute: number;
  durationMinutes: number;
  attendeeCount: number;
}

export interface GuestProposalReceipt {
  requestId: string;
  reference: string;
  status: 'submitted';
  resource: ProposalResourceSummary;
  bookingDate: string;
  startMinute: number;
  durationMinutes: number;
  attendeeCount: number;
  submissionPolicy: {
    reservesFacility: false;
    statusUpdatesByEmail: true;
    guestStatusPage: false;
  };
  createdAt: string;
}

export interface FacilityProposalQuote {
  rateCardVersionId: string;
  itemId: string;
  itemName: string;
  unit: RateCardUnit;
  unitAmountMinor: number;
  quantity: number;
  totalAmountMinor: number;
  requiredDepositAmountMinor: number;
  currency: 'USD';
}

export interface FacilityProposalHistoryEntry {
  id: string;
  fromStatus: FacilityProposalStatus | null;
  toStatus: FacilityProposalStatus;
  note: string;
  actorUserId: string | null;
  occurredAt: string;
}

export interface AdminFacilityProposal {
  id: string;
  reference: string;
  status: FacilityProposalStatus;
  version: number;
  resource: ProposalResourceSummary;
  customerId: string;
  contact: {
    name: string;
    email: string;
    phone: string;
  };
  organizationName: string;
  jobTitle: string;
  purpose: string;
  bookingDate: string;
  startMinute: number;
  durationMinutes: number;
  endMinute: number;
  attendeeCount: number;
  quote: FacilityProposalQuote | null;
  booking: {
    id: string;
    reference: string;
    totalAmountMinor: number;
    paidAmountMinor: number;
    balanceAmountMinor: number;
  } | null;
  rejectionReason: string;
  history: FacilityProposalHistoryEntry[];
  underReviewAt: string | null;
  awaitingPaymentAt: string | null;
  convertedAt: string | null;
  rejectedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ConvertFacilityProposalRequest {
  expectedVersion: number;
  expectedRateCardVersionId: string;
  expectedTotalAmountMinor: number;
  payment: {
    amountMinor: number;
    method: BookingPaymentMethod;
    methodDetail?: string;
  };
}

export type GuestProposalResourceListResponse = ApiSuccess<GuestProposalResource[]>;
export type GuestProposalReceiptResponse = ApiSuccess<GuestProposalReceipt>;
export type AdminFacilityProposalResponse = ApiSuccess<AdminFacilityProposal>;
export type AdminFacilityProposalListResponse = Paginated<AdminFacilityProposal>;
